import React, {
  ChangeEvent,
  MouseEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { General, MediaDevices } from 'icons/untitledui';
import { useAddShiftNoteAttachmentMutation } from 'shared/hooks/api/shiftNotes';
import { ReflexStep } from 'shiftNotes/_types';
import { usePosthogCapture } from 'shiftNotes/posthogCapture/shiftNotes';
import * as Styled from 'shiftNotes/styles/NotesStack.style';

interface NoteCardAskForPhotoProps {
  step: ReflexStep;
  noteId: string;
  reflexId: string;
}

const SUCCESS_FLASH_MS = 2000;

export default function NoteCardAskForPhoto({
  step,
  noteId,
  reflexId,
}: NoteCardAskForPhotoProps): JSX.Element {
  const { t } = useTranslation('pageShiftNotes');
  const shiftNotesCapture = usePosthogCapture();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { mutate: addAttachment, isPending } =
    useAddShiftNoteAttachmentMutation(noteId);

  useEffect(() => {
    if (!showSuccess) return undefined;
    const timer = window.setTimeout(
      () => setShowSuccess(false),
      SUCCESS_FLASH_MS
    );
    return () => window.clearTimeout(timer);
  }, [showSuccess]);

  const captureProperties = {
    shift_note_id: noteId,
    reflex_id: reflexId,
    step_id: step.id,
  };

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setErrorMessage(null);
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    addAttachment(formData, {
      onSuccess: () => {
        setShowSuccess(true);
        shiftNotesCapture.noteReflexPhotoAttached(captureProperties);
      },
      onError: () => {
        shiftNotesCapture.noteReflexPhotoError(captureProperties);
        setErrorMessage(t('instantReflex.photoUploadError'));
      },
    });
  };

  const pillLabel = (() => {
    if (isPending) return t('instantReflex.uploadingPhoto');
    if (showSuccess) return t('instantReflex.photoUploadSuccess');
    return t('instantReflex.addPhoto');
  })();

  const pillIcon = (() => {
    if (isPending) return <General.Loading02 />;
    if (showSuccess) return <General.Check />;
    return <MediaDevices.ImagePlus />;
  })();

  return (
    <>
      <Styled.StepActionRow>
        <Styled.StepActionPill
          type="button"
          onClick={handleClick}
          $isDisabled={isPending}
          aria-label={t('instantReflex.addPhoto')}
        >
          {pillIcon}
          {pillLabel}
        </Styled.StepActionPill>
      </Styled.StepActionRow>
      {errorMessage && (
        <Styled.StepErrorMessage role="alert">
          {errorMessage}
        </Styled.StepErrorMessage>
      )}
      <Styled.HiddenFileInput
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileChange}
      />
    </>
  );
}
